import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import {
  DEFAULT_COOKIE_PREFERENCES,
  getCookiePreferences,
  saveCookiePreferences,
  type CookiePreferences,
} from '../utils/cookies';

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_COOKIE_PREFERENCES);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const existing = getCookiePreferences();
    if (existing) {
      setPreferences(existing);
    } else {
      setPreferences(DEFAULT_COOKIE_PREFERENCES);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    const updated = {
      ...preferences,
      necessary: true,
      updatedAt: new Date().toISOString(),
    };
    saveCookiePreferences(updated);
    setPreferences(updated);
    setSavedAt(updated.updatedAt);
  };

  const handleReject = () => {
    const updated = {
      ...DEFAULT_COOKIE_PREFERENCES,
      updatedAt: new Date().toISOString(),
    };
    saveCookiePreferences(updated);
    setPreferences(updated);
    setSavedAt(updated.updatedAt);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-text">Cookie Settings</h2>
            <p className="mt-1 text-sm text-muted">Control optional cookies for analytics and marketing.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border bg-transparent px-3 py-1 text-sm text-muted hover:text-text hover:border-primary/60"
            aria-label="Close cookie settings"
          >
            Close
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-background/60 p-4">
            <div>
              <p className="font-semibold text-text">Essential cookies</p>
              <p className="text-sm text-muted">Required for security, login, and core functionality.</p>
            </div>
            <input type="checkbox" checked readOnly aria-label="Essential cookies enabled" className="h-4 w-4 accent-primary" />
          </div>

          <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-background/60 p-4">
            <div>
              <p className="font-semibold text-text">Analytics cookies</p>
              <p className="text-sm text-muted">We do not use analytics cookies right now.</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.analytics}
              onChange={(event) => setPreferences((prev) => ({ ...prev, analytics: event.target.checked }))}
              aria-label="Analytics cookies"
              className="h-4 w-4 accent-primary"
            />
          </div>

          <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-background/60 p-4">
            <div>
              <p className="font-semibold text-text">Marketing cookies</p>
              <p className="text-sm text-muted">We do not use marketing cookies right now.</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.marketing}
              onChange={(event) => setPreferences((prev) => ({ ...prev, marketing: event.target.checked }))}
              aria-label="Marketing cookies"
              className="h-4 w-4 accent-primary"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="primary" onClick={handleSave}>Save settings</Button>
            <Button variant="outline" onClick={handleReject}>Reject optional</Button>
          </div>
          {savedAt && (
            <p className="text-sm text-muted">Preferences saved on {new Date(savedAt).toLocaleString()}.</p>
          )}
        </div>
      </div>
    </div>
  );
};
